import React, { useEffect, useState } from "react"
import { Modal, ModalHeader, ModalBody, Card, CardBody, Row, Col } from "reactstrap"
import * as moment from "moment"
import TableLoader from "components/Common/TableLoader"
import { getReferredUserPerformanceCall } from "helpers/repository"
import { showErrorMessages } from "helpers/helpers"

const NA = "0"

const PALETTE = [
  { color: "#6366f1", bg: "#ede9fe" },
  { color: "#0891b2", bg: "#cffafe" },
  { color: "#16a34a", bg: "#dcfce7" },
  { color: "#2563eb", bg: "#dbeafe" },
  { color: "#d97706", bg: "#fef3c7" },
  { color: "#9333ea", bg: "#f3e8ff" },
  { color: "#dc2626", bg: "#fee2e2" },
]

const formatMoney = value =>
  Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

const formatDate = value => (value ? moment(value).format("DD/MM/YYYY") : 
'-')

const StatCard = ({ label, value, color, bg }) => (
  <Col sm={6} md={3} className="mb-3">
    <Card className="mb-0" style={{ height: "60px" }}>
      <CardBody
        style={{
          backgroundColor: bg,
          borderRadius: "8px",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: "6px 10px",
        }}
      >
        <div style={{ fontSize: "10px", color: "#6b7280", marginBottom: "2px" }}>
          {label}
        </div>
        <div
          style={{
            fontSize: "13px",
            fontWeight: 700,
            color,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={typeof value === "string" ? value : undefined}
        >
          {value ?? NA}
        </div>
      </CardBody>
    </Card>
  </Col>
)

const Section = ({ title, items }) => (
  <div className="mb-4">
    <h5 className="mb-3">{title}</h5>
    <Row>
      {items.map((item, index) => (
        <StatCard key={item.label} {...item} {...PALETTE[index % PALETTE.length]} />
      ))}
    </Row>
  </div>
)

function ReferredUserPerformanceModal({ isOpen, setIsOpen, referredUser }) {
  const [loading, setLoading] = useState(false)
  const [performance, setPerformance] = useState({})
  const toggle = () => setIsOpen(false)

  useEffect(() => {
    if (!isOpen || !referredUser?.id) return

    let cancelled = false
    const fetchPerformance = async () => {
      setLoading(true)
      try {
        const response = await getReferredUserPerformanceCall(referredUser.id)
        if (cancelled) return
        const payload = response?.data?.data || {}
        setPerformance(payload)
      } catch (error) {
        if (cancelled) return
        showErrorMessages(error)
        setPerformance({})
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchPerformance()
    return () => {
      cancelled = true
    }
  }, [isOpen, referredUser?.id])

 const {
   user = {},
   betting_metrics = {},
   engagement_metrics = {},
 } = performance


  const userInfoItems = [
    { label: "Username", value: user.display_name || "-" },
    { label: "User ID", value: user.id },
    { label: "Phone Number", value: user.phone_number },
    {
      label: "Registration Date",
      value: user.joined_at ? formatDate(user.joined_at) : NA,
    },
    { label: "Affiliate Referral Code", value: user.referred_by_code || NA },
    { label: "KYC Status", value: user.kyc_status || NA },
    { label: "Account Status", value: user.account_status || NA },
  ]

  const bettingMetricItems = [
    { label: "Number of Bets", value: betting_metrics.total_bets || NA },
    { label: "Number of Winning Bets", value: betting_metrics.winning_bets ?? NA },
    {
      label: "Average Stake",
      value:
        betting_metrics.average_stake != null
          ? formatMoney(betting_metrics.average_stake)
          : NA,
    },
    {
      label: "Largest Win",
      value:
        betting_metrics.largest_win != null
          ? formatMoney(betting_metrics.largest_win)
          : NA,
    },
    { label: "Current Active Bets", value: betting_metrics.active_bets || NA },
    { label: "Settled Bets", value: betting_metrics.settled_bets || NA },
  ]

  const engagementMetricItems = [
    {
      label: "Registration Source",
      value: engagement_metrics.registration_source || NA,
    },
    {
      label: "Total Wagered Amount",
      value: engagement_metrics.total_wagered != null ? formatMoney(engagement_metrics.total_wagered) : NA,
    },
    {
      label: "Total Winnings",
      value: engagement_metrics.total_winnings != null ? formatMoney(engagement_metrics.total_winnings) : NA,
    },
    {
      label: "First Deposit Amount",
      value:
        engagement_metrics.first_deposit_amount != null
          ? formatMoney(engagement_metrics.first_deposit_amount)
          : NA,
    },
    {
      label: "Total Deposits",
      value:
        engagement_metrics.total_deposits != null
          ? formatMoney(engagement_metrics.total_deposits)
          : NA,
    },
    {
      label: "Current Balance",
      value:
        engagement_metrics.current_balance != null
          ? formatMoney(engagement_metrics.current_balance)
          : NA,
    },
  ]

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" scrollable centered>
      <ModalHeader toggle={toggle} tag="div">
        <h5 className="modal-title mb-1">Referred User Performance</h5>
        <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
          Breakdown for{" "}
          <span className="fw-semibold">
            {user.display_name || user.phone_number || "-"}
          </span>
        </p>
      </ModalHeader>
      <ModalBody>
        {loading ? (
          <TableLoader />
        ) : (
          <>
            <Section title="User Information" items={userInfoItems} />
            <Section title="Betting Metrics" items={bettingMetricItems} />
            <Section title="Engagement Metrics" items={engagementMetricItems} />
          </>
        )}
      </ModalBody>
    </Modal>
  )
}

export default ReferredUserPerformanceModal
